import React from 'react';
import { getProject } from '../projects.config';

function ProjectPlaceholderPage({ projectId, C, Link, useBreakpoint }) {
	const { isMobile, isTablet, px } = useBreakpoint();
	const compact = isMobile || isTablet;
	const project = getProject(projectId);

	if (!project) return null;

	return (
		<div style={{ paddingTop: 100 }}>
			{/* Header */}
			<div style={{ padding: `60px ${px} 48px` }}>
				<Link
					to='/projects'
					style={{
						fontFamily: 'DM Mono',
						fontSize: 10,
						letterSpacing: '0.14em',
						textTransform: 'uppercase',
						color: '#4A6A66',
						textDecoration: 'none',
						display: 'inline-flex',
						alignItems: 'center',
						gap: 6,
						marginBottom: 32,
					}}
				>
					← All Projects
				</Link>

				<div
					style={{
						fontFamily: 'DM Mono',
						fontSize: 10,
						letterSpacing: '0.18em',
						textTransform: 'uppercase',
						color: '#4A6A66',
						marginBottom: 12,
					}}
				>
					{project.dates}
				</div>

				<h1
					className='display-heading fade-up'
					style={{
						fontSize: compact ? 'clamp(30px,7vw,52px)' : 'clamp(38px,5vw,64px)',
						maxWidth: 700,
						marginBottom: 16,
					}}
				>
					{project.name}
				</h1>

				<div
					style={{
						fontFamily: 'DM Mono',
						fontSize: 12,
						color: '#4A6A66',
						letterSpacing: '0.06em',
						marginBottom: 28,
					}}
				>
					{project.neighborhood} · {project.location}
				</div>

				<div
					style={{ width: 56, height: 3, background: '#1A2E2A', marginBottom: 28 }}
				/>

				<p
					style={{
						fontSize: compact ? 15 : 17,
						color: '#8AACAA',
						maxWidth: 660,
						lineHeight: 1.9,
					}}
				>
					{project.summary}
				</p>
			</div>

			{/* Research context */}
			<div style={{ background: '#070F0D', padding: `60px ${px}` }}>
				<div
					style={{
						display: 'grid',
						gridTemplateColumns: compact ? '1fr' : '1fr 1fr',
						gap: compact ? 48 : 80,
					}}
				>
					<div>
						<div
							style={{
								fontFamily: 'DM Mono',
								fontSize: 10,
								letterSpacing: '0.15em',
								textTransform: 'uppercase',
								color: C.tealLight,
								marginBottom: 16,
							}}
						>
							CDM Research Plan
						</div>
						<p
							style={{
								fontSize: 14,
								color: '#8AACAA',
								lineHeight: 1.9,
								marginBottom: 14,
							}}
						>
							{project.context}
						</p>
					</div>

					{/* Status tracker */}
					<div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
						{[
							{
								phase: 'Phase 1',
								title: 'Historical Scoping',
								status: 'In Progress',
								note: 'Establishing event timeline, geography, and source records',
							},
							{
								phase: 'Phase 2',
								title: 'Census Linkage',
								status: 'Planned',
								note: 'IPUMS microdata extraction and population baseline construction',
							},
							{
								phase: 'Phase 3',
								title: 'Disruption Analysis',
								status: 'Planned',
								note: 'Forward-linkage failure identification and household classification',
							},
							{
								phase: 'Phase 4',
								title: 'Community Validation',
								status: 'Planned',
								note: 'Descendant-led corroboration and archival cross-reference',
							},
						].map(({ phase, title, status, note }) => {
							const statusColor =
								status === 'Complete'
									? '#6DBF67'
									: status === 'In Progress'
										? C.tealLight
										: '#2A4A46';
							return (
								<div
									key={phase}
									style={{
										background: '#111E1C',
										border: '1px solid #1A2E2A',
										padding: '16px 20px',
										display: 'flex',
										gap: 14,
										alignItems: 'flex-start',
									}}
								>
									<div
										style={{
											fontFamily: 'DM Mono',
											fontSize: 9,
											color: '#4A6A66',
											letterSpacing: '0.1em',
											textTransform: 'uppercase',
											whiteSpace: 'nowrap',
											paddingTop: 3,
										}}
									>
										{phase}
									</div>
									<div style={{ flex: 1 }}>
										<div
											style={{
												fontWeight: 500,
												color: 'white',
												fontSize: 14,
												marginBottom: 3,
											}}
										>
											{title}
										</div>
										<div style={{ fontSize: 12, color: '#4A6A66' }}>{note}</div>
									</div>
									<div
										style={{
											fontFamily: 'DM Mono',
											fontSize: 9,
											letterSpacing: '0.08em',
											whiteSpace: 'nowrap',
											color: statusColor,
										}}
									>
										{status}
									</div>
								</div>
							);
						})}
					</div>
				</div>
			</div>

			{/* Methodology + CTA */}
			<div style={{ padding: `60px ${px} 80px` }}>
				<div
					style={{
						display: 'grid',
						gridTemplateColumns: compact ? '1fr' : '1fr 1fr',
						gap: compact ? 40 : 80,
						alignItems: 'center',
					}}
				>
					<div>
						<div className='section-label' style={{ marginBottom: 16 }}>
							Shared Framework
						</div>
						<p style={{ fontSize: 15, color: '#6A8A86', lineHeight: 1.9, marginBottom: 24 }}>
							When research begins in earnest, the {project.name} project will use the
							same census disruption methodology developed for Tulsa — five filters
							applied to IPUMS microdata to surface individuals whose forward linkage
							fails under conditions consistent with mass violence.
						</p>
						<Link to='/methodology' className='btn-outline'>
							Read the Methodology →
						</Link>
					</div>
					<div>
						<div className='section-label' style={{ marginBottom: 16 }}>
							Get Involved
						</div>
						<p style={{ fontSize: 15, color: '#6A8A86', lineHeight: 1.9, marginBottom: 24 }}>
							If you are a descendant, archivist, historian, or institution with
							knowledge of the {project.name}, we want to hear from you.
						</p>
						<a
							href='mailto:thecenterfortheuncounted@censusdisruption.org'
							className='btn-primary'
							style={{ textDecoration: 'none', display: 'inline-block' }}
						>
							Contact Us →
						</a>
					</div>
				</div>
			</div>

			{/* Back nav */}
			<div
				style={{
					borderTop: '1px solid #1A2E2A',
					padding: `32px ${px}`,
					display: 'flex',
					justifyContent: 'space-between',
					alignItems: 'center',
				}}
			>
				<Link to='/projects' className='btn-outline'>
					← Back to Projects
				</Link>
				<span
					style={{
						fontFamily: 'DM Mono',
						fontSize: 9,
						letterSpacing: '0.14em',
						textTransform: 'uppercase',
						color: '#2A4A46',
					}}
				>
					Research In Progress · Data Not Yet Available
				</span>
			</div>
		</div>
	);
}

export default ProjectPlaceholderPage;
